<template>
  <div class="autocomplete-wrapper">
    <el-input
      v-model="search"
      type="text"
      :placeholder="placeholder"
      :suffix-icon="search.length ? '' : icon"
      :clearable="isClearable"
      @focus="loadOptions = true"
      @clear="clearField"
    />
    <ul v-if="items.length" class="autocomplete">
      <li v-for="item in items" :key="item.id" @click="chooseItem(item)">
        {{ item.prematch }}<b class="highlighted">{{ item.match }}</b
        >{{ item.postmatch }}
      </li>
    </ul>
    <div v-else-if="checkOptionsLoading" class="autocomplete loader-wrapper">
      <vue-loaders name="ball-clip-rotate" :color="primaryColor" />
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import constants from '@/dependencies/constants'

export default {
  name: 'AutocompleteHighlight',
  props: {
    placeholder: {
      type: String,
      default: ''
    },
    icon: {
      type: String,
      default: ''
    },
    inputValue: {
      type: String,
      default: ''
    },
    isClearable: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      items: [],
      search: '',
      chosenItem: '',
      loadOptions: false,
      showList: true,
      minQueryLength: 3
    }
  },
  computed: {
    ...mapGetters({
      searchLocation: 'search/searchLocation',
      isMobile: 'common/getWidth'
    }),
    checkOptionsLoading() {
      return this.loadOptions && this.search.length >= this.minQueryLength
    },
    primaryColor() {
      return constants.COLORS.PRIMARY_COLOR
    }
  },
  watch: {
    search(str) {
      this.loadOptions && this.querySelections(str, this.highlightMatch)

      if (str.length <= this.minQueryLength) this.loadOptions = true
    },
    inputValue: {
      immediate: true,
      handler() {
        this.search = this.inputValue
      }
    },
    loadOptions(val) {
      if (val && this.search.length >= this.minQueryLength) {
        this.showList = true
        this.items = []
        this.querySelections(this.search, this.highlightMatch)
      }
    }
  },
  mounted() {
    document.addEventListener('click', this.closeList, false)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.closeList, false)
  },
  methods: {
    highlightMatch(item, searchQuery) {
      const title = item.title

      const titleLowercase = title.toLowerCase()
      const searchLowercase = searchQuery.toLowerCase()
      const matchStart = titleLowercase.search(searchLowercase)

      let prematch = ''
      let postmatch = ''

      if (matchStart > 0) {
        prematch = title.substr(0, matchStart)
      }
      if (matchStart + searchQuery.length < title.length) {
        postmatch = title.substr(matchStart + searchQuery.length)
      }
      const match = title.substr(matchStart, searchQuery.length)
      return {
        ...item,
        prematch,
        match,
        postmatch
      }
    },
    clearField() {
      this.chosenItem = {}
      this.$emit('emitSelection', null)

      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      })

      if (!this.isMobile) {
        //  reset sport or club in url
        this.$router.push({
          path: this.$router.path,
          params: {
            pathMatch: '',
            0: ''
          }
        })
      }
    },
    chooseItem(item) {
      this.loadOptions = false
      this.items = []
      this.search = item.title
      this.chosenItem = item
      this.$emit('emitSelection', item)
    },
    async querySelections(searchQuery, highlight) {
      if (searchQuery.length >= this.minQueryLength) {
        try {
          const {
            data: sportsClubsList
          } = await this.$api.SportApi.getSportsClubsList({
            params: {
              title: searchQuery,
              city_id: this.searchLocation
            }
          })
          this.items = []
          if (sportsClubsList.data.length) {
            for (const i in sportsClubsList.data) {
              const item = sportsClubsList.data[i]
              this.items.push(highlight(item, searchQuery))
            }
          } else {
            this.loadOptions = false
          }
        } catch (e) {
          // eslint-disable-next-line no-console
          console.log(e)
        }
      } else {
        this.items = []
        this.loadOptions = true
      }
    },
    closeList(el) {
      //  if click outside autocomplete component close list
      if (
        !(
          el.target.className === 'el-input__inner' ||
          el.target.className === 'highlighted'
        )
      ) {
        this.items = []
        this.loadOptions = false
        this.showList = false
      }
    }
  }
}
</script>

<style scoped lang="scss">
@import '~assets/css/variables.scss';
.autocomplete-wrapper {
  position: relative;
  margin-top: 20px;
  margin-bottom: 10px;
}
.autocomplete {
  position: absolute;
  width: 100%;
  max-height: 255px;
  overflow-y: auto;
  list-style: none;
  background-color: white;
  border: 1px solid $input-gray;
  border-top: none;
  z-index: 2;
  li {
    padding: 3px 15px;
    border-bottom: 1px solid #c8ebee;
    cursor: pointer;
    font-size: 16px;
    &:last-child {
      border-bottom: none;
    }
  }
}
.highlighted {
  color: $primary;
}

.loader-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  overflow: hidden;
}
</style>
